import React, { useMemo, useState } from "react";
import { usePageView } from "../../utils/track";
import { Link } from "react-router-dom";
import { ChevronDown, Trophy } from "lucide-react";
import { Badge, CompThumb, Loading, NoDataYet, RankChip, statusTone } from "../../components/ui";
import { useApi } from "../../api/client";
import { Competition, RankingRow } from "../../data/types";
import { formatPercent } from "../../utils/score";

/* Compact night-mode target — bullet impacts front and centre */
function ImpactTarget() {
  const shots = [
    { x: -22, y: 5, n: 1 }, { x: 9, y: -14, n: 2 }, { x: -5, y: 19, n: 3 },
    { x: 26, y: 12, n: 4 }, { x: -12, y: -26, n: 5 }, { x: 4, y: 3, n: 6, latest: true },
  ];
  const C = 130;
  return (
    <svg viewBox="0 0 260 260" className="w-full max-w-[250px]" aria-hidden>
      {[-90, -45, 0, 45, 90].map((g) => (
        <g key={g} stroke="#1E3A5F" strokeWidth="0.5">
          <line x1={C + g} y1="12" x2={C + g} y2="248" />
          <line x1="12" y1={C + g} x2="248" y2={C + g} />
        </g>
      ))}
      <circle cx={C} cy={C} r="112" fill="none" stroke="#334E68" strokeWidth="1.2" />
      <circle cx={C} cy={C} r="84" fill="none" stroke="#48647F" strokeWidth="1.2" />
      <circle cx={C} cy={C} r="57" fill="#2563EB" stroke="#3B82F6" strokeWidth="1.2" />
      <circle cx={C} cy={C} r="30" fill="#DC2626" stroke="#F87171" strokeWidth="1.2" />
      <line x1="12" y1={C} x2="248" y2={C} stroke="#5A7894" strokeWidth="0.7" />
      <line x1={C} y1="12" x2={C} y2="248" stroke="#5A7894" strokeWidth="0.7" />
      <g stroke="#4ADE80" strokeWidth="2.5" strokeLinecap="round">
        <line x1={C - 8} y1={C + 1} x2={C + 8} y2={C + 1} />
        <line x1={C} y1={C - 7} x2={C} y2={C + 9} />
      </g>
      {shots.map((s) => (
        <g key={s.n}>
          {s.latest && (
            <circle cx={C + s.x} cy={C + s.y} r="10" fill="none" stroke="#F8FAFC" strokeWidth="1.5" opacity="0.8">
              <animate attributeName="r" values="10;19;10" dur="2.4s" repeatCount="indefinite" />
              <animate attributeName="opacity" values="0.8;0;0.8" dur="2.4s" repeatCount="indefinite" />
            </circle>
          )}
          <circle cx={C + s.x} cy={C + s.y} r="9" fill="#DC2626" stroke="#F8FAFC" strokeWidth="1.8" />
          <text x={C + s.x} y={C + s.y + 3.2} textAnchor="middle" fontSize="8.5" fontWeight="700" fill="#fff">
            {s.n}
          </text>
        </g>
      ))}
    </svg>
  );
}

/*
 * The classes that lead, in the order they appear: Target Rifle, F-Open, F-TR.
 *
 * They are not the only ones shown — every class with somebody ranked gets a
 * board — but these three are what the Association is asked about most, so they
 * hold the first row and the newer disciplines follow behind them.
 */
const LEAD_CLASSES = ["TR", "FO", "FTR"];

export default function Home() {
  usePageView("home");
  const { data: competitions, loading } = useApi<Competition[]>("/competitions", []);
  /*
   * The top five of a SEASON, not of all time. A ranking that mixes this year
   * with three years ago flatters whoever shot most, and a competitor wants to
   * know where they stand now.
   */
  const { data: years } = useApi<number[]>("/ranking-years", []);
  // The newest season, always — the front page is about now.
  const [year] = useState<number | null>(null);
  const activeYear = year ?? years[0] ?? null;
  const { data: rankings } = useApi<RankingRow[]>(
    activeYear ? `/rankings?year=${activeYear}` : "/rankings",
    []
  );

  /*
   * Which boards are open. Open to begin with, so a competitor arriving at the
   * front page sees where they stand rather than a row of closed drawers; the
   * toggle is there for anyone who would rather fold a class away.
   */
  const [openClass, setOpenClass] = useState<Record<string, boolean>>({});
  /*
   * Board order. Target Rifle leads because it is the discipline the Association
   * is built around and the one most competitors are looking for; the F-Class
   * faces follow, then anything else alphabetically. Sorting the codes
   * alphabetically put F-Open first for no reason anybody would recognise.
   */
  /*
   * Every class with somebody ranked, three to a row.
   *
   * Fixing the front page at TR, FO and FTR meant a discipline could grow for a
   * whole season without appearing here — SH is the fastest growing of them and
   * would have stayed invisible until somebody edited this file. A class earns
   * its board by having a ranked competitor, which already takes the qualifying
   * number of record shots, so nothing empty can arrive.
   *
   * The three lead classes keep the first row; the rest follow by how many
   * competitors they have ranked, so the largest of the newer disciplines sits
   * closest to the top.
   */
  const frontClasses = useMemo(() => {
    const counts = new Map<string, number>();
    rankings.forEach((r) => {
      if (r.rifleClass) counts.set(r.rifleClass, (counts.get(r.rifleClass) || 0) + 1);
    });
    const lead = LEAD_CLASSES.filter((c) => counts.has(c));
    const rest = Array.from(counts.keys())
      .filter((c) => !LEAD_CLASSES.includes(c))
      .sort((a, b) => (counts.get(b) || 0) - (counts.get(a) || 0) || a.localeCompare(b));
    return [...lead, ...rest];
  }, [rankings]);

  // The detail fetch existed only for the leaderboard that has gone.
  // The headline leaderboard went with "Latest Results"; so did this request.

  /*
   * "Recent" and "upcoming" are different questions and now have a panel each.
   * A single list carrying both answered neither: a shoot three weeks away sat
   * above one whose results went up yesterday, under a heading saying recent.
   */
  // Ten each, a number the Association can hold in their head; the rest are on
  // the Events page behind "View All".
  const FRONT_PAGE_EVENTS = 10;
  /*
   * Recent counts back from today; upcoming counts forward to it. The API hands
   * them over newest first, which is what "recent" wants — but the nearest
   * UPCOMING shoot is the one being entered this week, so that list is turned
   * round rather than leading with whatever is furthest away.
   */
  const shotOn = (c: Competition) => c.endDateISO || c.startDateISO || "";
  const recentEvents = competitions.filter((c) => c.status !== "UPCOMING").slice(0, FRONT_PAGE_EVENTS);
  const upcoming = competitions
    .filter((c) => c.status === "UPCOMING")
    .slice()
    .sort((a, b) => shotOn(a).localeCompare(shotOn(b)))
    .slice(0, FRONT_PAGE_EVENTS);

  return (
    <div>
      {/* Compact banner — full background photo visible, no duplicate nav buttons */}
      <section className="relative overflow-hidden bg-navy-950">
        <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: "url(/images/hero-bg.png)" }} />
        <div className="absolute inset-0 bg-gradient-to-r from-navy-950 via-navy-950/60 to-transparent" />
        <div className="relative mx-auto flex min-h-[120px] max-w-[1400px] flex-col justify-center px-5 py-5">
          <h1 className="max-w-xl text-2xl font-extrabold leading-tight tracking-tight text-white md:text-3xl">
            Official Match Results
          </h1>
          <p className="mt-1.5 max-w-md text-[14px] text-slate-200">
            Competition results, rankings and statistics from all registered shooting events.
          </p>
        </div>
      </section>

      <div className="mx-auto max-w-[1400px] px-4 py-4 sm:px-5">
        {loading ? (
          <div className="card">
            <Loading label="Loading competitions..." />
          </div>
        ) : !competitions.length ? (
          <div className="card">
            <NoDataYet what="results" />
          </div>
        ) : (
          <>
        {/*
          Recent Events and Upcoming Events, side by side.

          A "Latest Results" leaderboard used to sit here, showing one event's
          top five. With two shoots running it read as the results rather than
          as a sample of one of them, which is exactly the confusion a results
          service exists to prevent.
        */}
        <div className="grid min-w-0 grid-cols-1 gap-4 lg:grid-cols-2">
          <div className="card">
            <div className="flex items-center justify-between border-b border-line px-4 py-2.5">
              <h2 className="text-sm font-bold text-ink">Recent Events</h2>
              <Link to="/competitions" className="text-[13px] font-semibold text-primary hover:underline">
                View All
              </Link>
            </div>
            <ul className="divide-y divide-line">
              {recentEvents.map((c) => (
                <li key={c.id}>
                  <Link to={`/competitions/${c.id}`} className="flex items-center gap-3 px-4 py-2 transition-colors hover:bg-slate-50">
                    <CompThumb kind={c.thumb} size={34} />
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-[13px] font-bold text-ink">{c.name}</div>
                      <div className="truncate text-xs text-muted">
                        {c.startDate}
                        {c.endDate && c.endDate !== c.startDate ? ` – ${c.endDate}` : ""} · {c.range}
                      </div>
                    </div>
                    <Badge tone={statusTone(c.status)}>{c.status}</Badge>
                  </Link>
                </li>
              ))}
            </ul>
          </div>
          <div className="card">
            <div className="flex items-center justify-between border-b border-line px-4 py-2.5">
              <h2 className="text-sm font-bold text-ink">Upcoming Events</h2>
              <Link to="/competitions" className="text-[13px] font-semibold text-primary hover:underline">
                View All
              </Link>
            </div>
            <ul className="divide-y divide-line">
              {upcoming.map((c) => (
                <li key={c.id}>
                  <Link to={`/competitions/${c.id}`} className="flex items-center gap-3 px-4 py-2 transition-colors hover:bg-slate-50">
                    <CompThumb kind={c.thumb} size={30} />
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-[13px] font-bold text-ink">{c.name}</div>
                      <div className="text-xs text-muted">{c.startDate} · {c.city}, {c.stateCode}</div>
                    </div>
                    <span className="shrink-0 text-[11px] font-bold text-primary">{c.statusDetail}</span>
                  </Link>
                </li>
              ))}
              {!upcoming.length && <li className="px-4 py-6 text-center text-[13px] text-muted">No upcoming competitions scheduled.</li>}
            </ul>
          </div>
        </div>

        {/*
          One board per class, three to a row.

          Side by side because they do not rank against each other: a Target
          Rifle 98% and an F-Open 98% are different achievements, and stacking
          them in one column implies the top one matters most. Columns say they
          are equals, which they are.

          Three to a row rather than three in total, so a class that grows into
          the sport arrives here by itself. The row wraps: twenty classes would
          be seven rows, and each board is still only five names deep.
        */}
        <div className="mt-4 grid grid-cols-1 gap-3 lg:grid-cols-3">
          {!frontClasses.length && (
            <div className="card px-4 py-8 text-center text-[13px] text-muted lg:col-span-3">
              Rankings appear once competitors have shot the qualifying number of record shots.
            </div>
          )}
          {frontClasses.map((cls) => {
            /*
             * Numbered within the class, not across every class at once.
             *
             * `rank` is the competitor's position in the whole portal, so the
             * Target Rifle board opened at 2, 3, 5, 6, 7 — Ray Wilson shown with
             * a silver chip on a board he leads, while the Rankings page had him
             * first. Two pages, two answers, and one of them was nonsense.
             */
            const rows = rankings
              .filter((r) => r.rifleClass === cls)
              .slice(0, 5)
              .map((r, i) => ({ ...r, place: r.classRank ?? i + 1 }));
            const open = openClass[cls] !== false;
            return (
              <div key={cls} className="card min-w-0">
                <div className="flex items-center justify-between gap-2 border-b border-line px-3 py-2.5">
                  <button
                    onClick={() => setOpenClass((o) => ({ ...o, [cls]: !open }))}
                    className="flex min-w-0 items-center gap-1.5 text-sm font-bold text-ink"
                    aria-expanded={open}
                  >
                    <ChevronDown size={15} className={`shrink-0 text-muted transition-transform ${open ? "" : "-rotate-90"}`} />
                    <Trophy size={15} className="shrink-0 text-warning" />
                    {/* The season is named, not implied: "current" is only true
                        until January. */}
                    <span className="truncate">
                      Top 5 · {cls}
                      {activeYear ? <span className="font-semibold text-muted"> · {activeYear}</span> : null}
                    </span>
                  </button>
                  <Link to="/rankings" className="shrink-0 text-[12px] font-semibold text-primary hover:underline">
                    Full Rankings
                  </Link>
                </div>
                {open && (
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-line bg-slate-50">
                        <th className="th">#</th>
                        <th className="th">Shooter</th>
                        <th className="th text-right">Overall</th>
                        {/* Their own best single result — not the best in the class. */}
                        <th className="th text-right">Personal Best</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-line">
                      {!rows.length && (
                        <tr>
                          <td colSpan={4} className="td py-6 text-center text-[13px] text-muted">
                            Nobody qualified yet.
                          </td>
                        </tr>
                      )}
                      {rows.map((r, i) => (
                        <tr
                          key={r.sid}
                          onClick={() => window.open(`/shooters/${r.sid}`, "_blank", "noopener")}
                          className={`cursor-pointer transition-colors hover:bg-primary-soft/40 ${i % 2 === 1 ? "bg-slate-50/50" : ""}`}
                        >
                          <td className="td"><RankChip rank={r.place} /></td>
                          <td className="td min-w-0">
                            {/* Club and grade sit under the name: a third of the page
                                is not wide enough to give each of them a column. */}
                            <Link
                              to={`/shooters/${r.sid}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="block truncate font-bold text-ink hover:text-primary"
                            >
                              {r.shooter}
                            </Link>
                            <div className="truncate text-[11px] text-muted">
                              {[r.club, r.grade].filter(Boolean).join(" · ")}
                            </div>
                          </td>
                          <td className="td text-right font-bold">{formatPercent(r.avgPercent)}</td>
                          <td className="td text-right font-bold text-primary">{formatPercent(r.bestPercent)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            );
          })}
        </div>

        {/* Bullet impacts — shot-by-shot detail for every match / string / shooter */}
        <section className="card mt-4 overflow-hidden">
          <div className="grid items-center lg:grid-cols-[300px_1fr]">
            <div className="flex items-center justify-center bg-navy-950 p-4">
              <ImpactTarget />
            </div>
            <div className="p-5">
              <h2 className="mt-1 text-lg font-extrabold tracking-tight text-ink">
                Every impact plotted for every match, string and shooter
              </h2>
              <p className="mt-1.5 max-w-2xl text-[13px] leading-relaxed text-muted">
                Each string from results for matches entered is plotted on an interactive target map — impact positions,
                V/X centres, velocities and ballistics (group size, mean radius, radial SD). Open any match result and
                follow the target icon to a shooter's shot impacts and relevant statistics.
              </p>
            </div>
          </div>
        </section>
          </>
        )}
      </div>
    </div>
  );
}
